import React, { useRef, useMemo, useCallback } from 'react';
import type { ProductSwitcherProduct } from 'product-switcher-web/UserProductApp';
import {
  Layout,
  message,
  getFileUrl,
  SidebarMenu,
  AccountItem,
  useAutoLogout,
  useCurrentBreakpoint,
} from 'design-system';

import Footer from 'src/components/common/Footer';

import { AccountSecurityMenu } from 'src/types/account';

import Main from './main';
import { NAV } from '../config-layout';
import { useRouter } from '../../routes/hooks';
import Logo from '../../assets/images/logo.png';
import { useNavData } from './config-navigation';
import { useAuthContext } from '../../auth/hooks';
import { User, Product } from '../../services/user';
import { useBoolean } from '../../hooks/use-boolean';
import { getDeviceId, getUserFullName } from '../../utils';
import securityIcon from '../../assets/icons/security.svg';
import WatermarkWrap from '../../components/watermark/WatermarkWrap';
import { STORAGE_KEY, STORAGEREFRESHTOKENKEY } from '../../auth/context/jwt/auth-provider';

const AccountCenter = React.lazy(() => import('account-center-web/AccountCenter'));
const ProductSwitcher = React.lazy(() => import('product-switcher-web/UserProductApp'));
const currentProduct = Product.AMOEBA;
const biProduct: ProductSwitcherProduct = { id: 'BI', name: 'BI', roles: [] };

const { Content } = Layout;

// ----------------------------------------------------------------------

type Props = {
  children: React.ReactNode;
};

export default function DashboardLayout({ children }: Props) {
  const collapsed = useBoolean();
  const router = useRouter();
  const { user, logout, getUserProfile } = useAuthContext();
  const deviceId = getDeviceId();
  const token = localStorage.getItem(STORAGE_KEY) || '';
  const refreshToken = localStorage.getItem(STORAGEREFRESHTOKENKEY) || '';
  const productSwitcherProducts = useMemo<ProductSwitcherProduct[]>(() => {
    const products: ProductSwitcherProduct[] = user?.products ?? [];
    return products.some((product) => product.name === 'BI') ? products : [...products, biProduct];
  }, [user?.products]);
  const navData = useNavData();
  const breakpoint = useCurrentBreakpoint();
  const isMini = useMemo(() => breakpoint === 'xs' || breakpoint === 'sm', [breakpoint]);
  const accountCenterRef = useRef<{
    handleOnClick: (defaultMenu?: AccountSecurityMenu) => void;
  }>(null);
  const handleLogout = async () => {
    try {
      await logout();
      router.replace('/');
    } catch (error) {
      // 错误处理
      message.alert({ content: 'Unable to logout!', type: 'error' });
    }
  };

  const onLogout = useCallback(() => {
    const LOGOUT_BROADCAST_KEY = 'logoutAt';
    localStorage.setItem(LOGOUT_BROADCAST_KEY, String(Date.now()));
    logout();
  }, [logout]);

  // 自动登出
  useAutoLogout({
    enabled: true,
    onLogout,
  });

  return (
    <WatermarkWrap>
      <Layout style={{ minHeight: '100vh' }}>
        <SidebarMenu
          width={NAV.W_VERTICAL}
          backgroundColor="#105FC8"
          style={{
            height: '100vh',
            display: isMini ? 'none' : 'block',
          }}
          collapsedStyles={{ color: 'white' }}
          logo={Logo}
          collapsedOnToggle={collapsed.onToggle}
          collapsed={collapsed.value}
          collapsedWidth={102}
          headerTitle={
            <div
              style={{
                fontWeight: 'bold',
                fontSize: '20px',
                color: 'white',
                whiteSpace: 'nowrap',
                fontVariationSettings: 'opsz auto',
                fontFeatureSettings: 'kern on',
              }}
            >
              经营管理
            </div>
          }
          navProps={{
            data: navData,
            slotProps: {
              currentRole: user?.role,
              navBackGroudColor: '#3c7cd3',
              collapsed: collapsed.value,
            },
          }}
          accountCenterProps={{
            logout: () => {
              handleLogout();
            },
            rootWrapStyles: { backgroundColor: 'rgb(234, 242, 252)' },
            accountName: user?.username,
            accountFullName: getUserFullName(user as User),
            accountAvatar: getFileUrl(user?.avatar),
            userInfo: user?.email,
            menu: {
              items: [
                {
                  type: 'divider',
                },
                {
                  key: 'account',
                  label: (
                    // eslint-disable-next-line jsx-a11y/click-events-have-key-events,jsx-a11y/no-static-element-interactions
                    <div
                      onClick={() => accountCenterRef.current?.handleOnClick('device-management')}
                    >
                      <AccountItem src={securityIcon}>账号安全</AccountItem>
                    </div>
                  ),
                  popupStyle: {
                    width: 95,
                  },
                  children: [],
                },
              ],
            },
            userInfoItem: {
              clickCallBack: () => accountCenterRef.current?.handleOnClick('account-general'),
            },
            productSwitcher: (
              <React.Suspense fallback={null}>
                <ProductSwitcher
                  token={token}
                  deviceId={deviceId}
                  currentProduct={currentProduct}
                  products={productSwitcherProducts}
                  refreshToken={refreshToken}
                  onUnauthorized={onLogout}
                />
              </React.Suspense>
            ),
          }}
        />
        <Content
          style={{
            transition: 'width 0.2s ease',
            height: '100vh',
            overflow: 'auto',
            scrollbarWidth: 'thin',
            scrollbarColor: '#DADEE5',
            minHeight: 280,
          }}
        >
          {/* account center初始化 */}
          <AccountCenter
            product={currentProduct}
            token={token}
            refreshToken={refreshToken}
            deviceId={deviceId}
            accountCenterRef={accountCenterRef}
            onUpdateUserSuccess={getUserProfile}
          />

          <Main>{children}</Main>
          <Footer />
        </Content>
      </Layout>
    </WatermarkWrap>
  );
}
